/**
 * Ledige tider for kundemøter (CRM-4.4, VIB-2987). Ren logikk uten nett: tar opptatt-tidene
 * fra calendar-freebusy og gir ukerutenettet i arbeidstiden (hverdager 08:00 til 16:00 Oslo,
 * ikke helligdager, 30 minutters trinn) og hvilke verter som er ledige til et gitt tidspunkt.
 */
import { dateKey, isOpenDay, osloLocalToDate, osloParts } from "./send-window";

export interface BusyInterval {
  start: string;
  end: string;
}

export type HostErrorCode = "no_access" | "unavailable";

export interface FreeBusyHost {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  busy: BusyInterval[];
  error?: HostErrorCode;
}

const DEFAULTS = { startHour: 8, endHour: 16, stepMinutes: 30 } as const;
const DAY_MS = 86_400_000;

function overlaps(busy: readonly BusyInterval[], startMs: number, endMs: number): boolean {
  return busy.some((b) => new Date(b.start).getTime() < endMs && new Date(b.end).getTime() > startMs);
}

export type HostStatus = "free" | "busy" | "no_access" | "unavailable";

export const HOST_STATUS_LABEL: Record<HostStatus, string> = {
  free: "Ledig",
  busy: "Opptatt",
  no_access: "Ingen kalendertilgang",
  unavailable: "Kalenderen svarer ikke",
};

export interface HostAvailability {
  host: FreeBusyHost;
  status: HostStatus;
}

/** Hvem er ledige i hele [start, end)? Verter uten kalendertilgang er aldri «ledige». */
export function hostsAvailableAt(hosts: readonly FreeBusyHost[], start: Date, end: Date): HostAvailability[] {
  return hosts.map((host) => {
    if (host.error) return { host, status: host.error };
    return { host, status: overlaps(host.busy, start.getTime(), end.getTime()) ? "busy" : "free" };
  });
}

/** Mandagen (Oslo, YYYY-MM-DD) i uken der første møte kan bookes: neste uke når det er helg eller fredag etter arbeidstid. */
export function initialWeekMonday(now: Date, endHour: number = DEFAULTS.endHour): string {
  const p = osloParts(now);
  const fromMonday = (p.weekday + 6) % 7;
  const weekDone = p.weekday === 0 || p.weekday === 6 || (p.weekday === 5 && p.hour * 60 + p.minute >= endHour * 60 - DEFAULTS.stepMinutes);
  return shiftDateKey(dateKey(p), weekDone ? 7 - fromMonday : -fromMonday);
}

/** YYYY-MM-DD flyttet `days` dager frem (eller tilbake). */
export function shiftDateKey(key: string, days: number): string {
  const [y, m, d] = key.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d) + days * DAY_MS);
  return dateKey({ year: t.getUTCFullYear(), month: t.getUTCMonth() + 1, day: t.getUTCDate() });
}

/** ISO-ukenummeret til en dato (YYYY-MM-DD). */
export function isoWeek(key: string): number {
  const [y, m, d] = key.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + 3 - ((t.getUTCDay() + 6) % 7));
  const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((t.getTime() - firstThursday.getTime()) / DAY_MS - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

/** Freebusy-spørringen for uken: mandag 00:00 til fredag 23:59 Oslo. */
export function weekRange(monday: string): { start: string; end: string } {
  const [y, m, d] = monday.split("-").map(Number);
  const [fy, fm, fd] = shiftDateKey(monday, 4).split("-").map(Number);
  return { start: osloLocalToDate(y, m, d, 0).toISOString(), end: osloLocalToDate(fy, fm, fd, 23, 59).toISOString() };
}

export type WeekCellState = "free" | "busy" | "past" | "closed";

export interface WeekCell {
  start: Date;
  /** Oslo-klokkeslett, HH:MM. */
  time: string;
  state: WeekCellState;
  /** Vertene som har plass til hele møtet fra denne starten. */
  freeHosts: FreeBusyHost[];
}

export interface WeekDay {
  dateKey: string;
  /** Helg eller fridag (company_holidays): ingen ruter kan velges. */
  closed: boolean;
  cells: WeekCell[];
}

export interface WeekGridOptions {
  monday: string;
  hosts: readonly FreeBusyHost[];
  durationMinutes: number;
  holidays: Set<string>;
  /** Bare denne verten; null er alle verter. */
  hostId?: string | null;
  notBefore?: Date;
  startHour?: number;
  endHour?: number;
  stepMinutes?: number;
}

/**
 * Ukerutenettet i Book møte: mandag til fredag, én rute per starttid i arbeidstiden.
 * En rute er ledig når minst én vert (eller den valgte verten) har plass til hele møtet.
 * Ruter der møtet ville gått forbi arbeidstidens slutt er opptatt.
 */
export function weekGrid(opts: WeekGridOptions): WeekDay[] {
  const startHour = opts.startHour ?? DEFAULTS.startHour;
  const endHour = opts.endHour ?? DEFAULTS.endHour;
  const step = opts.stepMinutes ?? DEFAULTS.stepMinutes;
  const candidates = opts.hosts.filter((h) => !h.error && (!opts.hostId || h.user_id === opts.hostId));
  return [0, 1, 2, 3, 4].map((offset) => {
    const key = shiftDateKey(opts.monday, offset);
    const [y, m, d] = key.split("-").map(Number);
    const closed = !isOpenDay(osloParts(osloLocalToDate(y, m, d, 12)), opts.holidays);
    const cells: WeekCell[] = [];
    for (let minute = startHour * 60; minute < endHour * 60; minute += step) {
      const start = osloLocalToDate(y, m, d, Math.floor(minute / 60), minute % 60);
      const time = `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
      const endMs = start.getTime() + opts.durationMinutes * 60_000;
      let state: WeekCellState;
      let freeHosts: FreeBusyHost[] = [];
      if (closed) state = "closed";
      else if (opts.notBefore && start.getTime() < opts.notBefore.getTime()) state = "past";
      else {
        if (minute + opts.durationMinutes <= endHour * 60) freeHosts = candidates.filter((h) => !overlaps(h.busy, start.getTime(), endMs));
        state = freeHosts.length > 0 ? "free" : "busy";
      }
      cells.push({ start, time, state, freeHosts });
    }
    return { dateKey: key, closed, cells };
  });
}
